import { useRef } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@alchemer/ui/components/dialog';
import { Button } from '@alchemer/ui/components/button';
import { Loader2 } from '@alchemer/ui/icons';
import type { ComponentProps } from 'react';
import { tw } from '@/lib/tailwind';

type ButtonVariant = ComponentProps<typeof Button>['variant'];

interface ModalDialogProps {
  /** The React element that triggers the modal when clicked. Optional when using controlled state with 'open' prop */
  trigger?: React.ReactNode;
  /** The main content body of the modal */
  body: React.ReactNode;
  /** Optional label for the action button. If not provided, no action button is rendered */
  actionBtnLabel?: string;
  /**
   * Optional handler for the action button click.
   * Receives a closeModal function that can be called to programmatically close the modal.
   * If validation fails, don't call closeModal to keep the modal open.
   */
  actionBtnHandler?: (closeModal: () => void) => void;
  /** Whether to disable the action button. Useful for loading states */
  disableActionButton?: boolean;
  /** When true, replaces the action button label with a spinner and loading text */
  actionBtnLoading?: boolean;
  /** Label shown next to the spinner when actionBtnLoading is true. Defaults to 'Saving...' */
  actionBtnLoadingLabel?: string;
  /**
   * The variant of the action button. Maps to the Button component variants.
   * Use `'destructive'` for irreversible actions like deleting.
   * Defaults to `'default'`.
   */
  actionBtnVariant?: ButtonVariant;
  /** Optional title displayed in the modal header */
  title?: string;
  /** Whether to show the built-in close X button. Defaults to true */
  showCloseButton?: boolean;
  /** Whether the modal should be open by default. Defaults to false */
  defaultOpen?: boolean;
  /** Controlled open state. When provided, the component becomes controlled */
  open?: boolean;
  /** Optional container element to render the modal portal into. Defaults to document.body */
  container?: HTMLElement;
  /** Callback fired when the modal open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Additional className applied to DialogContent for size/layout overrides */
  className?: string;
  /** Called when the dialog opens and attempts to focus its first element. Call e.preventDefault() to take manual control of focus. */
  onOpenAutoFocus?: (e: Event) => void;
}

/**
 * A reusable modal dialog component built on top of the Alchemer UI Dialog components.
 *
 * Features:
 * - Customizable trigger element
 * - Optional header with title
 * - Flexible body content
 * - Optional action button with validation support
 * - Programmatic modal closing
 * - Portal rendering with custom container support
 * - Open state change callbacks
 *
 * @example
 * Basic usage:
 * ```tsx
 * <ModalDialog
 *   trigger={<button>Open Modal</button>}
 *   title="Confirm Action"
 *   body={<p>Are you sure you want to continue?</p>}
 *   actionBtnLabel="Confirm"
 *   actionBtnHandler={(closeModal) => {
 *     // Perform action
 *     console.log('Action confirmed');
 *     closeModal();
 *   }}
 * />
 * ```
 *
 * @example
 * With form validation:
 * ```tsx
 * <ModalDialog
 *   trigger={<button>Create Dataset</button>}
 *   title="Name Dataset"
 *   body={<input name="datasetName" />}
 *   actionBtnLabel="Create"
 *   actionBtnHandler={(closeModal) => {
 *     const isValid = validateForm();
 *     if (isValid) {
 *       createDataset();
 *       closeModal(); // Only close if validation passes
 *     }
 *   }}
 *   onOpenChange={(open) => {
 *     if (!open) resetForm(); // Reset form when modal closes
 *   }}
 * />
 * ```
 */

const ModalDialog = ({
  trigger,
  body,
  title,
  showCloseButton = true,
  actionBtnLabel,
  actionBtnHandler,
  actionBtnVariant = 'default',
  disableActionButton = false,
  actionBtnLoading = false,
  actionBtnLoadingLabel = 'Saving...',
  defaultOpen = false,
  open,
  container,
  onOpenChange,
  className,
  onOpenAutoFocus,
}: ModalDialogProps) => {
  const closeRef = useRef<HTMLButtonElement>(null);

  const handleActionClick = () => {
    if (actionBtnHandler) {
      const closeModal = () => {
        closeRef.current?.click();
      };
      actionBtnHandler(closeModal);
    }
  };

  return (
    <Dialog
      {...(open !== undefined ? { open } : { defaultOpen })}
      onOpenChange={onOpenChange}
    >
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogPortal container={container ?? document.body}>
        <DialogOverlay
          className={tw(
            `bg-background-950/25 data-[state=open]:animate-in data-[state=closed]:animate-out`,
            `data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-9998 duration-150`,
          )}
        />
        <DialogContent
          showCloseButton={showCloseButton}
          onOpenAutoFocus={onOpenAutoFocus}
          className={tw(
            `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0`,
            `data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95`,
            `data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] top-[50%] left-[50%]`,
            `bg-card z-9999 translate-x-[-50%] translate-y-[-50%] rounded-lg p-6 shadow-lg duration-150 sm:max-w-2xl`,
            className,
          )}
        >
          {title && (
            <DialogHeader>
              <DialogTitle className="text-left text-xl font-semibold">{title}</DialogTitle>
            </DialogHeader>
          )}
          {body}
          <DialogFooter className="justify-end">
            <DialogClose asChild>
              <Button
                ref={closeRef}
                variant="outline"
              >
                Cancel
              </Button>
            </DialogClose>
            {actionBtnLabel && actionBtnHandler ? (
              <Button
                variant={actionBtnVariant}
                onClick={handleActionClick}
                disabled={disableActionButton || actionBtnLoading}
              >
                {actionBtnLoading ? (
                  <span className={tw('flex items-center gap-1.5')}>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {actionBtnLoadingLabel}
                  </span>
                ) : (
                  actionBtnLabel
                )}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export { ModalDialog };
